import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FormattedMessage, injectIntl } from 'react-intl';

import { Button } from 'shared/components';
import { cutNationalPrefixFromPhoneNumber, formatCodeNumber } from 'modules/user/utils/formatPhoneNumber';
import { getTranslationByKeyFromMap } from 'modules/user/utils/getTranslationByKeyFromMap';

import { messagesMap } from '../../constants/messageMap';
import { userFieldLabels } from '../../constants/userFieldLabels';
import UserDetailsAccordion from '../UserDetailsAccordion';
import UserDetailsItem from './components/UserDetailsItem';

import theme from './UserDetailsDetails.scss';

const propTypes = {
  intl: PropTypes.object.isRequired,
  title: PropTypes.string,
  fullName: PropTypes.string,
  gender: PropTypes.string,
  email: PropTypes.string,
  phoneNumber: PropTypes.string,
  policyId: PropTypes.string,
  address: PropTypes.string,
  description: PropTypes.string,
  dateOfBirth: PropTypes.number,
  invokeEditUserModal: PropTypes.func.isRequired,
  userReadPermissions: PropTypes.object.isRequired,
  userUpdatePermissions: PropTypes.object.isRequired,
  userFieldConfig: PropTypes.object,
};

const defaultProps = {
  title: '',
  fullName: '',
  gender: '',
  email: '',
  phoneNumber: '',
  address: '',
  description: '',
  policyId: '',
  dateOfBirth: '',
  userFieldConfig: {},
};

const UserDetailsDetails = ({
  intl,
  title,
  fullName,
  gender,
  policyId,
  email,
  phoneNumber,
  dateOfBirth,
  address,
  description,
  invokeEditUserModal,
  userReadPermissions,
  userUpdatePermissions,
  userFieldConfig,
}) => (
  <UserDetailsAccordion
    header={
      <FormattedMessage
        id="usersAndGroupsManagement.userDetailsDetails.header"
        defaultMessage="** Details"
      />
    }
    hasBorderRadius={ true }
  >
    <div className={ theme.root }>
      <div className={ theme.detailsContainer }>
        <If condition={ userReadPermissions.canSeeName }>
          <UserDetailsItem
            message={ userFieldLabels.title }
            value={ title }
            valueHandler={ getTranslationByKeyFromMap(intl, messagesMap) }
          />
        </If>

        <If condition={ userReadPermissions.canSeeName }>
          <UserDetailsItem
            message={ userFieldLabels.name }
            value={ fullName }
          />
        </If>

        <If condition={ userReadPermissions.canSeeGender }>
          <UserDetailsItem
            message={ userFieldLabels.gender }
            value={ gender }
            valueHandler={ getTranslationByKeyFromMap(intl, messagesMap) }
          />
        </If>

        <If condition={ userReadPermissions.canSeeBirthDate }>
          <UserDetailsItem
            message={ userFieldLabels.dateOfBirth }
            value={ moment(dateOfBirth).isValid() ? moment(dateOfBirth).format('MMMM Do, YYYY') : '' }
          />
        </If>

        <If condition={ userReadPermissions.canSeeEmail }>
          <UserDetailsItem
            message={ userFieldLabels.email }
            value={ email }
          />
        </If>

        <If condition={ userReadPermissions.canSeePhone }>
          <UserDetailsItem
            message={ userFieldLabels.phoneNumber }
            isPhoneNumber={ true }
            value={ cutNationalPrefixFromPhoneNumber(phoneNumber) }
            valueHandler={ formatCodeNumber }
          />
        </If>

        <If condition={ userReadPermissions.canSeePolicyId }>
          <UserDetailsItem
            message={ userFieldConfig.externalIdLabel }
            value={ policyId }
          />
        </If>

        <If condition={ userReadPermissions.canSeeAddress }>
          <UserDetailsItem
            message={ userFieldLabels.address }
            value={ address }
          />
        </If>

        <If condition={ userReadPermissions.canSeeDescription }>
          <UserDetailsItem
            message={ userFieldLabels.description }
            value={ description }
          />
        </If>
      </div>

      <If condition={ userUpdatePermissions.canUpdateUserData && userUpdatePermissions.canUpdateUserAccount }>
        <div className={ theme.detailsFooter }>
          <Button
            testId="button-edit-details"
            label={ intl.formatMessage({ id: 'MyAccount.Edit.Button', defaultMessage: '** Edit' }) }
            iconName="pencil"
            size="small"
            onClick={ invokeEditUserModal }
          />
        </div>
      </If>
    </div>
  </UserDetailsAccordion>
);

UserDetailsDetails.propTypes = propTypes;
UserDetailsDetails.defaultProps = defaultProps;

export default injectIntl(UserDetailsDetails);
